import React, { useState } from 'react';
import { Typography, Box, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { MONTH_ORDER, calculateCurrentFiscalYear, type TrialBalanceRow } from './constants';

type Props = {
  dedupedRows: TrialBalanceRow[];
  fiscalYear: number;
  currentMonth: number;
};

/**
 * 12ヶ月推移グラフ（棒=売上高の積み上げ／折れ線=営業利益・前年売上）。
 * 当月（月末未到達）の棒は点線枠の暫定値として描画する。
 */
export default function MonthlyTrendChart({ dedupedRows, fiscalYear, currentMonth }: Props) {
  // 表示モード（単月 / 累積=年度頭からの累計）
  const [displayMode, setDisplayMode] = useState<'monthly' | 'cumulative'>('monthly');

  const getMonthSum = (month: number, category: string, suffix: 'actual' | 'budget' | 'actual_prev' = 'actual') => {
    return dedupedRows
      .filter(r => r.category_l2_name === category)
      .reduce((sum, r) => sum + (Number(r[`month_${month}_${suffix}`]) || 0), 0);
  };

  const chartData = MONTH_ORDER.map(m => {
    const sales = getMonthSum(m, '純売上高');
    const cogs = getMonthSum(m, '完成工事原価');
    const sga = getMonthSum(m, '販売費及び一般管理費計');
    const grossProfit = sales - cogs;            // 売上総利益 (粗利)
    const operatingProfit = grossProfit - sga;

    const salesBudget = getMonthSum(m, '純売上高', 'budget');
    const cogsBudget = getMonthSum(m, '完成工事原価', 'budget');
    const sgaBudget = getMonthSum(m, '販売費及び一般管理費計', 'budget');
    const profitBudget = salesBudget - cogsBudget - sgaBudget;

    // 前年同月の純売上高実績
    const salesPrev = getMonthSum(m, '純売上高', 'actual_prev');

    // 達成率 (実績 / 計画 × 100) ゼロ除算回避
    const salesAchievementRate = salesBudget !== 0 ? (sales / salesBudget) * 100 : null;
    const profitAchievementRate = profitBudget !== 0 ? (operatingProfit / Math.abs(profitBudget)) * 100 : null;
    // 粗利率 (粗利 / 売上 × 100) 単月
    const grossMarginRate = sales !== 0 ? (grossProfit / sales) * 100 : null;

    return {
      month: `${m}月`,
      monthNum: m,
      sales_actual: sales,          // 円単位の生値
      profit_actual: operatingProfit,
      sales_budget: salesBudget,
      profit_budget: profitBudget,
      sales_prev: salesPrev,        // 前年同月の売上実績
      gross_actual: grossProfit,    // 粗利実績 (積み上げ下部)
      cogs_actual: cogs,            // 原価実績 (積み上げ上部)
      sales_achievement_rate: salesAchievementRate,
      profit_achievement_rate: profitAchievementRate,
      gross_margin_rate: grossMarginRate,
    };
  });

  // 累積データ（年度頭=9月からの累計）。率（粗利率・達成率）は累積値から再計算する
  const cumulativeData = (() => {
    const acc = {
      sales_budget: 0,
      gross_actual: 0,
      cogs_actual: 0,
      sales_prev: 0,
      profit_budget: 0,
      profit_actual: 0,
    };
    return chartData.map((d) => {
      acc.sales_budget += d.sales_budget;
      acc.gross_actual += d.gross_actual;
      acc.cogs_actual += d.cogs_actual;
      acc.sales_prev += d.sales_prev;
      acc.profit_budget += d.profit_budget;
      acc.profit_actual += d.profit_actual;
      const cumSales = acc.gross_actual + acc.cogs_actual; // 累積売上実績 = 累積粗利 + 累積原価
      return {
        month: d.month,
        monthNum: d.monthNum,
        sales_actual: cumSales,
        profit_actual: acc.profit_actual,
        sales_budget: acc.sales_budget,
        profit_budget: acc.profit_budget,
        sales_prev: acc.sales_prev,
        gross_actual: acc.gross_actual,
        cogs_actual: acc.cogs_actual,
        sales_achievement_rate: acc.sales_budget !== 0 ? (cumSales / acc.sales_budget) * 100 : null,
        profit_achievement_rate: acc.profit_budget !== 0 ? (acc.profit_actual / Math.abs(acc.profit_budget)) * 100 : null,
        gross_margin_rate: cumSales !== 0 ? (acc.gross_actual / cumSales) * 100 : null,
      };
    });
  })();

  // 実際に描画するデータ（単月 or 累積）
  const data = displayMode === 'cumulative' ? cumulativeData : chartData;

  const formatYAxis = (value: number) => value.toLocaleString();

  // 月末未到達 (暫定) 判定。実日付基準で、表示年度が当年度の場合のみ今日の月以降を対象とする
  // (過去年度はすべて確定値、未来年度はすべて暫定値)
  const actualFiscalYear = calculateCurrentFiscalYear();
  const actualMonthIdx = MONTH_ORDER.indexOf(new Date().getMonth() + 1);
  const isProvisionalMonth = (monthNum: number) => {
    if (fiscalYear < actualFiscalYear) return false;
    if (fiscalYear > actualFiscalYear) return true;
    return actualMonthIdx >= 0 && MONTH_ORDER.indexOf(monthNum) >= actualMonthIdx;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <Box sx={{
        backgroundColor: '#334155',
        color: '#fff',
        borderRadius: '8px',
        px: 1.5,
        py: 1,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.2)',
        minWidth: '140px',
      }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>
          {label}
        </Typography>
        {(() => {
          // 売上実績 = 粗利 (gross_actual) + 残り (cogs_actual) の積み上げ合計
          const grossEntry = payload.find((p: any) => p.dataKey === 'gross_actual');
          const cogsEntry = payload.find((p: any) => p.dataKey === 'cogs_actual');
          const salesActualTotal = (grossEntry || cogsEntry)
            ? (Number(grossEntry?.value) || 0) + (Number(cogsEntry?.value) || 0)
            : undefined;

          return payload.map((entry: any, idx: number) => {
            // 上部 (cogs_actual) は原価額のため単体表示しない。売上実績合計として別途表示する
            if (entry.dataKey === 'cogs_actual') return null;
            return (
              <React.Fragment key={idx}>
                {/* 粗利 (gross_actual) の直前に売上実績合計を表示 (売上高実績 → 売上総利益 の順) */}
                {entry.dataKey === 'gross_actual' && salesActualTotal !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: '#93c5fd' }} />
                      <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
                        売上高実績
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
                      {salesActualTotal.toLocaleString()}円
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '2px',
                      backgroundColor: entry.color,
                    }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
                      {entry.name}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>
                    {entry.value.toLocaleString()}円
                  </Typography>
                </Box>
              </React.Fragment>
            );
          });
        })()}
        {/* 達成率 (実績 / 計画) payload の各系列値から直接算出 */}
        {(() => {
          const findVal = (key: string) => {
            const e = payload.find((p: any) => p.dataKey === key);
            return e ? Number(e.value) : undefined;
          };
          // 売上実績は積み上げ棒 (粗利 + 残り) から再構成
          const grossActual = findVal('gross_actual');
          const cogsActual = findVal('cogs_actual');
          const salesActual = (grossActual !== undefined || cogsActual !== undefined)
            ? (grossActual ?? 0) + (cogsActual ?? 0) : undefined;
          const salesBudget = findVal('sales_budget');
          const profitActual = findVal('profit_actual');
          const profitBudget = findVal('profit_budget');

          const salesRate = (salesActual !== undefined && salesBudget !== undefined && salesBudget !== 0)
            ? (salesActual / Math.abs(salesBudget)) * 100 : null;
          const profitRate = (profitActual !== undefined && profitBudget !== undefined && profitBudget !== 0)
            ? (profitActual / Math.abs(profitBudget)) * 100 : null;

          if (salesRate === null && profitRate === null) return null;

          return (
            <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #475569', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              {salesRate !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: salesRate >= 100 ? '#4ade80' : '#f87171' }}>
                    売上高達成率
                  </Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: salesRate >= 100 ? '#4ade80' : '#f87171' }}>
                    {salesRate.toFixed(1)}%
                  </Typography>
                </Box>
              )}
              {profitRate !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: profitRate >= 100 ? '#4ade80' : '#f87171' }}>
                    営業利益達成率
                  </Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: profitRate >= 100 ? '#4ade80' : '#f87171' }}>
                    {profitRate.toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
            12ヶ月推移（売上・営業利益）
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.5 }}>
            {displayMode === 'cumulative'
              ? '棒グラフ 売上高（累積）/ 折れ線 営業利益（累積）（点線枠の棒は当月＝月末未到達の暫定値）'
              : '棒グラフ 売上高 / 折れ線 営業利益（点線枠の棒は当月＝月末未到達の暫定値）'}
          </Typography>
        </Box>
        {/* 単月 / 累積 切替 */}
        <RadioGroup
          row
          value={displayMode}
          onChange={(e) => setDisplayMode(e.target.value as 'monthly' | 'cumulative')}
          sx={{ gap: 0.5, '& .MuiFormControlLabel-root': { mr: 1 } }}
        >
          <FormControlLabel
            value="monthly"
            control={<Radio size="small" />}
            label={<Typography sx={{ fontSize: '13px' }}>単月</Typography>}
          />
          <FormControlLabel
            value="cumulative"
            control={<Radio size="small" />}
            label={<Typography sx={{ fontSize: '13px' }}>累積</Typography>}
          />
        </RadioGroup>
      </Box>

      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={data} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: '#64748b' }}
            axisLine={{ stroke: '#cbd5e1' }}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickFormatter={formatYAxis}
            axisLine={{ stroke: '#cbd5e1' }}
            tickLine={false}
            width={100}
            label={{ value: '売上高 (円)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickFormatter={formatYAxis}
            axisLine={{ stroke: '#cbd5e1' }}
            tickLine={false}
            width={100}
            label={{ value: '営業利益 (円)', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#64748b' } }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} />
          <Legend
            wrapperStyle={{ fontSize: '12px', paddingBottom: '8px' }}
            iconType="square"
            verticalAlign="top"
            align="right"
          />
          <Bar
            yAxisId="left"
            dataKey="sales_budget"
            name="売上高計画"
            fill="#dbeafe"
            radius={[4, 4, 0, 0]}
          />
          {/* 売上高実績 = 粗利 (下・濃青) + 残り (上・中青) の青系積み上げ。全体の高さ = 売上実績 */}
          <Bar
            yAxisId="left"
            dataKey="gross_actual"
            name="売上総利益"
            stackId="actual"
            fill="#3b82f6"
          >
            {data.map((entry, idx) => (
              <Cell
                key={`cell-gross-${idx}`}
                fill={entry.monthNum === currentMonth ? '#1d4ed8' : '#3b82f6'}
                fillOpacity={isProvisionalMonth(entry.monthNum) ? 0.45 : 1}
                stroke={isProvisionalMonth(entry.monthNum) ? '#94a3b8' : undefined}
                strokeWidth={isProvisionalMonth(entry.monthNum) ? 1.5 : undefined}
                strokeDasharray={isProvisionalMonth(entry.monthNum) ? '4 2' : undefined}
              />
            ))}
          </Bar>
          <Bar
            yAxisId="left"
            dataKey="cogs_actual"
            name="売上高実績"
            stackId="actual"
            fill="#93c5fd"
            radius={[4, 4, 0, 0]}
          >
            {data.map((entry, idx) => (
              <Cell
                key={`cell-cogs-${idx}`}
                fill="#93c5fd"
                fillOpacity={isProvisionalMonth(entry.monthNum) ? 0.45 : 1}
                stroke={isProvisionalMonth(entry.monthNum) ? '#94a3b8' : undefined}
                strokeWidth={isProvisionalMonth(entry.monthNum) ? 1.5 : undefined}
                strokeDasharray={isProvisionalMonth(entry.monthNum) ? '4 2' : undefined}
              />
            ))}
            {/* 棒全体 (=売上実績) の上に粗利率を表示 */}
            <LabelList
              dataKey="gross_margin_rate"
              position="top"
              formatter={(value: any) =>
                value === null || value === undefined ? '' : `粗利 ${Number(value).toFixed(1)}%`
              }
              style={{ fontSize: 10, fontWeight: 700, fill: '#1e293b' }}
            />
          </Bar>
          {/* 前年売上 (左軸=売上) */}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="sales_prev"
            name="前年売上"
            stroke="#94a3b8"
            strokeWidth={2}
            dot={{ fill: '#94a3b8', r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="profit_budget"
            name="営業利益計画"
            stroke="#10b981"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: '#10b981', r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="profit_actual"
            name="営業利益実績"
            stroke="#10b981"
            strokeWidth={2.5}
            dot={{ fill: '#10b981', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
